import { Component, EventEmitter, Input, OnChanges, OnInit, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LIST_DATA_PROVIDER } from '@luzula/luzula-lib';
import { ReliefInfoModalComponent } from '../help/relief-info-modal.component';
import { BLOCK_VIEWBOX, type Facette } from '../help/gelaende-block';
import { raumbildFacetten } from '../help/woelbung-raumbild';
import { neigungFacettenFuerStufe } from '../help/neigung-raumbild';
import { einbindungFacetten } from '../help/einbindung-raumbild';
import { hanganschlussFacetten } from '../help/hanganschluss-raumbild';
import type { WoelbungForm } from '../help/woelbung-symbol';

/** Themen, die ein Schraegbild haben und deshalb ueber diesen Knopf laufen. */
export type ReliefThema = 'Woelbung' | 'Neigungsstufe' | 'ReliefformUmgebung' | 'Hanganschluss';

/** Eine Zeile aus der jeweiligen Liste. Welche Felder sie fuehrt, haengt am Thema. */
interface ReliefEintrag {
	id: string;
	label: string;
	vertikal?: WoelbungForm;
	horizontal?: WoelbungForm;
}

const AUFFORDERUNG: Record<ReliefThema, string> = {
	Woelbung: 'Wölbung wählen …',
	Neigungsstufe: 'Neigung wählen …',
	ReliefformUmgebung: 'Einbindung wählen …',
	Hanganschluss: 'Hanganschluss wählen …',
};

/**
 * Auswahl einer Relief-Angabe mit Bild statt als Klappliste.
 *
 * Ein natives `<select>` kann in seinen Zeilen nur Text zeigen, kein Bild -
 * deshalb geht es hier wie bei den Punktarten (`bp-punktart-picker`): Ein Knopf
 * zeigt die aktuelle Wahl mit ihrem Schraegbild, ein Klick oeffnet das
 * Relief-Fenster, in dem jede Zeile ihr Bild traegt und angetippt werden kann.
 * Damit steht die Erklaerung gleich beim Auswaehlen.
 *
 * Klartext und gueltige Werte kommen aus derselben Liste wie beim alten
 * Auswahlfeld (`LIST_DATA_PROVIDER`, Tabelle wie das Thema heisst).
 */
@Component({
	standalone: true,
	selector: 'bp-relief-picker',
	imports: [CommonModule],
	template: `
		<button type="button" class="form-control relief-trigger" (click)="oeffnen()" [attr.title]="aktuell?.label || aufforderung">
			<svg *ngIf="facetten.length" class="trigger-bild" [attr.viewBox]="viewBox" aria-hidden="true">
				<path *ngFor="let facette of facetten" [attr.d]="facette.d" [attr.fill]="facette.farbe" [attr.stroke]="facette.kante" />
			</svg>
			<span *ngIf="aktuell" class="trigger-text">{{ aktuell.label }}</span>
			<span *ngIf="!aktuell && model" class="trigger-text text-danger">{{ model }} (unbekannt)</span>
			<span *ngIf="!aktuell && !model" class="text-muted">{{ aufforderung }}</span>
			<span *ngIf="model" class="leeren" role="button" tabindex="0" title="Angabe entfernen"
				(click)="leeren($event)" (keydown.enter)="leeren($event)">&#10005;</span>
			<i class="bi bi-chevron-down text-secondary"></i>
		</button>
	`,
	styles: [`
		/* Der Knopf steht in einer Formularzeile neben den alten Auswahlfeldern
		   und soll dieselbe Breite und Hoehe haben wie sie: Der Host ist deshalb
		   ein Block ueber die ganze Zelle, nicht so breit wie sein Inhalt.
		   Die Regeln tragen zusaetzlich .form-control, damit sie gegen die
		   Formular-Vorgaben der App (.touch-form .form-control) gewinnen. */
		:host {
			display: block;
			width: 100%;
		}
		.relief-trigger.form-control {
			display: flex;
			align-items: center;
			gap: 0.5rem;
			width: 100%;
			text-align: left;
			cursor: pointer;
			min-height: 40px;
			padding: 2px 6px;
			/* Derselbe Rahmen wie bei den Auswahlfeldern daneben: Die
			   Eingabe-Bauteile der Bibliothek zeichnen ihn eckig und dunkel
			   (controls/styles/input.component.css), nicht abgerundet und
			   hell wie ein gewoehnlicher Knopf. */
			border-radius: 0;
			border: 1px solid darkslategray;
		}
		.trigger-bild {
			width: 36px;
			height: 35px;
			flex: 0 0 auto;
		}
		.trigger-bild path {
			stroke-width: 0.35;
		}
		.trigger-text {
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
		.leeren {
			margin-left: auto;
			padding: 0 0.25rem;
			color: #6c757d;
		}
		.leeren:hover {
			color: #dc3545;
		}
		.bi-chevron-down {
			margin-left: auto;
		}
		.leeren + .bi-chevron-down {
			margin-left: 0;
		}
	`],
})
export class ReliefPickerComponent implements OnInit, OnChanges {
	/** Welche Angabe gewaehlt wird. Bestimmt Liste, Fenster und Bild. */
	@Input() thema: ReliefThema = 'Woelbung';

	/**
	 * Gewaehlter Wert (das Kuerzel oder der Name aus der Liste), leer wenn
	 * nichts angegeben ist.
	 *
	 * Steht hier ein Wert, den die Liste nicht kennt (Altdaten), nennt der Knopf
	 * ihn rot beim Namen. Ihn einfach nicht anzuzeigen waere schlimmer: Im
	 * Datensatz stuende er weiter, und niemand wuesste davon.
	 */
	@Input() model?: string;
	@Output() modelChange = new EventEmitter<string | undefined>();

	readonly viewBox = BLOCK_VIEWBOX;

	eintraege: ReliefEintrag[] = [];
	aktuell?: ReliefEintrag;
	/** Einmal gerechnet, nicht bei jedem Durchlauf der Aenderungserkennung. */
	facetten: Facette[] = [];

	private modalService = inject(NgbModal);
	private listen = inject(LIST_DATA_PROVIDER);

	get aufforderung(): string {
		return AUFFORDERUNG[this.thema] ?? 'Wählen …';
	}

	ngOnInit(): void {
		this.ladeListe();
		this.aktualisiere();
	}

	ngOnChanges(): void {
		// Das Thema kann sich im Betrieb nicht aendern; die Liste wird deshalb
		// nur geladen, wenn sie noch fehlt.
		if (this.eintraege.length === 0) this.ladeListe();
		this.aktualisiere();
	}

	/** Fenster mit den Stufen oeffnen; ein Antippen liefert den Listenwert. */
	oeffnen(): void {
		const modalRef = this.modalService.open(ReliefInfoModalComponent, { centered: true, scrollable: true });
		modalRef.componentInstance.topicName = this.thema;
		modalRef.componentInstance.aktuellerWert = this.model;
		modalRef.result.then(
			(wert: string) => this.setze(wert),
			() => undefined, // weggeklickt, alles bleibt
		);
	}

	/** Angabe wieder entfernen - das leere Feld gab es in der Klappliste auch. */
	leeren(event: Event): void {
		event.stopPropagation();
		this.setze(undefined);
	}

	private setze(wert: string | undefined): void {
		this.model = wert;
		this.aktualisiere();
		this.modelChange.emit(wert);
	}

	private ladeListe(): void {
		const roh = (this.listen.getHardcoded(this.thema) || []) as (string | ReliefEintrag)[];
		// Manche Listen sind reine Zeichenketten (Einbindung), andere Objekte.
		this.eintraege = roh.map(e => (typeof e === 'string' ? { id: e, label: e } : e));
	}

	private aktualisiere(): void {
		this.aktuell = this.eintraege.find(e => e.id === this.model);
		this.facetten = this.aktuell ? this.bildFuer(this.aktuell) : [];
	}

	private bildFuer(eintrag: ReliefEintrag): Facette[] {
		switch (this.thema) {
			case 'Woelbung':
				return eintrag.vertikal && eintrag.horizontal ? raumbildFacetten(eintrag.vertikal, eintrag.horizontal) : [];
			case 'Neigungsstufe':
				return neigungFacettenFuerStufe(eintrag.id);
			case 'ReliefformUmgebung':
				return einbindungFacetten(eintrag.id);
			case 'Hanganschluss':
				return hanganschlussFacetten(eintrag.id);
			default:
				return [];
		}
	}
}
